/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface SideNavTitleProps extends Omit<ComponentProps<'div'>, 'title'> {
  title: ReactNode
  /** Muted uppercase subtitle below the title row. */
  description?: ReactNode
  /** Content aligned to the end of the title row (e.g. icon buttons). */
  actions?: ReactNode
}

export function SideNavTitle({ title, description, actions, className, ...rest }: SideNavTitleProps) {
  return (
    <div className={cn('relative flex w-full flex-col items-start gap-0 py-2', className)} {...rest}>
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-x-4 inset-y-0 border-border border-b bg-card/60"
      />
      <div className="relative flex w-full items-center justify-between gap-2">
        <h2 className="font-semibold text-foreground text-xl tracking-wide md:text-2xl">{title}</h2>
        {actions}
      </div>
      {description != null ? (
        <p className="relative font-semibold text-muted-foreground text-xs uppercase tracking-wider">{description}</p>
      ) : null}
    </div>
  )
}
